import React, { useMemo, useState } from 'react';

export interface SparklinePoint {
  date: string;
  value: number;
  secondary?: number;
  label?: string;
}

interface SparklineProps {
  points: SparklinePoint[];
  height?: number;
  strokeClassName?: string;
  fillClassName?: string;
  meanLineClassName?: string;
  /** Render a second polyline from each point's `secondary` value.
   *  The series is normalized to its OWN max so its shape is visible
   *  even when its scale dwarfs the primary series. */
  secondaryStrokeClassName?: string;
  secondaryFillClassName?: string;
  /** Tooltip formatter for hovered points. */
  formatTooltip?: (p: SparklinePoint) => string;
}

/** Exposed by useSparklineSummary so callers can render legends/headers
 *  outside the chart with the right max values. */
export const computeSparklineMax = (points: SparklinePoint[]) => {
  const primaryMax = Math.max(0, ...points.map((p) => p.value));
  const secondaryVals = points
    .map((p) => p.secondary)
    .filter((v): v is number => v != null);
  const secondaryMax = secondaryVals.length > 0 ? Math.max(0, ...secondaryVals) : 0;
  return { primaryMax, secondaryMax, hasSecondary: secondaryVals.length > 0 };
};

/**
 * Minimal inline-SVG sparkline. No chart library. Renders a smoothed
 * polyline of `value` across `points`, a faint area fill underneath,
 * a dashed mean line, and a filled marker on the latest point. Hover
 * over any data point to surface a small tooltip.
 */
export const Sparkline: React.FC<SparklineProps> = ({
  points,
  height = 64,
  strokeClassName = 'stroke-blue-600 dark:stroke-blue-400',
  fillClassName = 'fill-blue-500/10 dark:fill-blue-400/20',
  meanLineClassName = 'stroke-gray-400/60 dark:stroke-gray-500/60',
  secondaryStrokeClassName = 'stroke-emerald-600 dark:stroke-emerald-400',
  secondaryFillClassName = 'fill-emerald-500/10 dark:fill-emerald-400/15',
  formatTooltip,
}) => {
  const width = 1000; // viewBox width — scales fluidly via CSS
  // Top padding has to clear the SECONDARY_OFFSET (10px) the secondary
  // line is shifted up by, otherwise its peak renders above the viewBox.
  const padding = { top: 16, right: 4, bottom: 4, left: 4 };
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const {
    polyline,
    areaPath,
    meanY,
    xy,
    secondaryPolyline,
    secondaryAreaPath,
    hasSecondary,
  } = useMemo(() => {
    if (points.length === 0) {
      return {
        polyline: '',
        areaPath: '',
        meanY: 0,
        xy: [],
        secondaryAreaPath: '',
        mean: 0,
        max: 0,
        secondaryPolyline: '',
        secondaryMax: 0,
        hasSecondary: false,
      };
    }
    const primaryMax = Math.max(0, ...points.map((p) => p.value));
    const secondaryVals = points
      .map((p) => p.secondary)
      .filter((v): v is number => v != null);
    const secMax = secondaryVals.length > 0 ? Math.max(0, ...secondaryVals) : 0;
    // Both series share a Y axis so equal values render at equal heights.
    // The secondary series is typically the dominant scale (e.g. total
    // dispatches dwarfs rejections), so the shared max keeps the primary
    // visually proportional to it.
    const maxVal = Math.max(1, primaryMax, secMax);
    const meanVal =
      points.reduce((s, p) => s + p.value, 0) / Math.max(1, points.length);
    const usableW = width - padding.left - padding.right;
    const usableH = height - padding.top - padding.bottom;
    const step = points.length > 1 ? usableW / (points.length - 1) : 0;
    const coords = points.map((p, i) => {
      const x = padding.left + i * step;
      const y = padding.top + (1 - p.value / maxVal) * usableH;
      return { x, y, p };
    });
    const polyStr = coords.map((c) => `${c.x},${c.y}`).join(' ');
    const areaStr = `M ${coords[0].x},${padding.top + usableH} ` +
      coords.map((c) => `L ${c.x},${c.y}`).join(' ') +
      ` L ${coords[coords.length - 1].x},${padding.top + usableH} Z`;
    const meanYVal = padding.top + (1 - meanVal / maxVal) * usableH;
    let secPolyStr = '';
    let secAreaStr = '';
    if (secondaryVals.length > 0) {
      // Shift the secondary line up by 10px so it remains visible when its
      // value equals the primary (otherwise the two would overlap exactly
      // on 100%-reject days).
      const SECONDARY_OFFSET = 10;
      const secCoords = points.map((p, i) => {
        const x = padding.left + i * step;
        const v = p.secondary ?? 0;
        const y =
          padding.top + (1 - v / maxVal) * usableH - SECONDARY_OFFSET;
        return { x, y };
      });
      secPolyStr = secCoords.map((c) => `${c.x},${c.y}`).join(' ');
      const bottomY = padding.top + usableH;
      secAreaStr =
        `M ${secCoords[0].x},${bottomY} ` +
        secCoords.map((c) => `L ${c.x},${c.y}`).join(' ') +
        ` L ${secCoords[secCoords.length - 1].x},${bottomY} Z`;
    }
    return {
      polyline: polyStr,
      areaPath: areaStr,
      meanY: meanYVal,
      xy: coords,
      mean: meanVal,
      max: maxVal,
      secondaryPolyline: secPolyStr,
      secondaryAreaPath: secAreaStr,
      secondaryMax: secMax,
      hasSecondary: secondaryVals.length > 0,
    };
  }, [points, height]);

  if (points.length === 0) {
    return (
      <div
        style={{ height }}
        className="w-full flex items-center justify-center text-xs text-muted"
      >
        No data in this window.
      </div>
    );
  }

  const hovered = hoverIdx != null ? xy[hoverIdx] : null;

  return (
    <div className="relative w-full" style={{ height }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full h-full"
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Area fill */}
        <path d={areaPath} className={fillClassName} />
        {/* Mean line */}
        <line
          x1={padding.left}
          x2={width - padding.right}
          y1={meanY}
          y2={meanY}
          strokeDasharray="3 3"
          className={meanLineClassName}
        />
        {/* Secondary area + polyline. Drawn behind primary so the primary
            line sits on top when they cross. Shifted up 10px so it stays
            visible when both series have the same value. */}
        {hasSecondary && (
          <>
            <path d={secondaryAreaPath} className={secondaryFillClassName} />
            <polyline
              points={secondaryPolyline}
              fill="none"
              strokeWidth={1.5}
              className={secondaryStrokeClassName}
            />
          </>
        )}
        {/* Primary polyline */}
        <polyline
          points={polyline}
          fill="none"
          strokeWidth={1.5}
          className={strokeClassName}
        />
        {/* Latest-point marker */}
        {xy.length > 0 && (
          <circle
            cx={xy[xy.length - 1].x}
            cy={xy[xy.length - 1].y}
            r={3}
            className="fill-blue-600 dark:fill-blue-400"
          />
        )}
        {/* Hover hit-targets — invisible wide rects */}
        {xy.map((c, i) => {
          const stepW = width / xy.length;
          return (
            <rect
              key={i}
              x={c.x - stepW / 2}
              y={0}
              width={stepW}
              height={height}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
            />
          );
        })}
        {/* Hovered marker */}
        {hovered && (
          <circle
            cx={hovered.x}
            cy={hovered.y}
            r={3.5}
            className="fill-blue-700 dark:fill-blue-300"
          />
        )}
      </svg>
      {hovered && (
        <div
          className="absolute -translate-x-1/2 -translate-y-full z-[100] bg-gray-900 text-white text-[0.6875rem] px-2 py-1 rounded shadow pointer-events-none whitespace-nowrap"
          style={{
            left: `${(hovered.x / width) * 100}%`,
            top: Math.max(0, hovered.y - 4),
          }}
        >
          {formatTooltip
            ? formatTooltip(hovered.p)
            : hovered.p.label || `${hovered.p.date}: ${hovered.p.value}`}
        </div>
      )}
    </div>
  );
};

export default Sparkline;
